import {Button, Flex, Text} from '@chakra-ui/react';
import {History} from 'lucide-react';
import {formatDisplayDateTime} from '../domain/naming';
import {NumberedRevision} from '../domain/revisions';

export interface OlderVersionNoticeProps {
    revision: NumberedRevision;
    onBack: () => void;
    /** Phones: one line that never wraps, without the "Korábbi verzió:" label; only the date truncates. */
    compact?: boolean;
}

// Below this width (of the space the notice may take) the label and the date would be cut to almost
// nothing, so only the number stays.
const NARROW = '@container older-version (max-width: 260px)';
const hiddenWhenNarrow = {[NARROW]: {display: 'none'}};

/**
 * Says, over the picture, that an older version of the photo is shown, with a way back to the current
 * one. Where space runs out, the button first wraps under the text (centred), then the date
 * truncates, and at last only the icon, the number and the button remain; the icon never shrinks.
 */
export function OlderVersionNotice({revision, onBack, compact = false}: OlderVersionNoticeProps) {
    const modified = new Date(revision.revision.modifiedTime);
    const date = Number.isNaN(modified.getTime()) ? undefined : formatDisplayDateTime(modified);

    // The full width the notice may take, so the container query sees the space, not the notice.
    return <Flex w="100%" justify="center" pointerEvents="none" css={{containerType: 'inline-size', containerName: 'older-version'}}>
        <Flex
            align="center"
            justify="center"
            wrap={compact ? 'nowrap' : 'wrap'}
            columnGap={compact ? 1 : 2}
            rowGap={1}
            maxW="100%"
            bg="blackAlpha.800"
            color="white"
            fontSize="sm"
            pl={3}
            pr={2}
            py={2}
            borderRadius="md"
            pointerEvents="auto"
            // A tap here is not one of the picture's gestures.
            onPointerDown={(e) => e.stopPropagation()}
            role="status"
        >
            {/* One flex item, so the icon never wraps away from the text. On one line (phones) its parts
                are the notice's own items instead, so only the date shrinks and nothing covers the icon. */}
            <Flex
                display={compact ? 'contents' : 'flex'}
                align="center"
                gap={1}
                minW={0}
                maxW="100%"
                pr={2}
                whiteSpace="nowrap"
                fontVariantNumeric="tabular-nums"
            >
                <History size={16} style={{flexShrink: 0}}/>
                {!compact && <Text flexShrink={0} ml={1} css={hiddenWhenNarrow}>Korábbi verzió:</Text>}
                <Text flexShrink={0} ml={compact ? 1 : 0}>#{revision.number}</Text>
                {date && <Text minW={0} truncate css={compact ? undefined : hiddenWhenNarrow}>· {date}</Text>}
            </Flex>
            <Button size="xs" variant="subtle" flexShrink={0} ml={compact ? 1 : 0} onClick={onBack}>
                Vissza a legújabbhoz
            </Button>
        </Flex>
    </Flex>;
}
